import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Contract, ContractStatus, ContractType } from '../models/contract';
import { ImmobilierService } from '../services/immobilier.service';
import { AddEditContratDialogComponent } from './add-edit-contrat-dialog/add-edit-contrat-dialog.component';
import { ContratDetailDialogComponent } from './contrat-detail-dialog/contrat-detail-dialog.component';
import { ExcelService } from '../excel.service';
import { PdfService } from '../services/pdf.service';


@Component({
  selector: 'app-contrats',
  templateUrl: './contrats.component.html',
  styleUrls: ['./contrats.component.css']
})
export class ContratsComponent implements OnInit {

  displayedColumns: string[] = ['contractNumber', 'type', 'propertyReference', 'ownerName', 'tenantBuyerName', 'startDate', 'status', 'actions'];
  dataSource: MatTableDataSource<Contract>;
  contracts: Contract[] = [];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  // Filters
  filterType: string = '';
  filterStatus: string = '';
  searchText: string = '';

  // Pagination
  pageSize = 10;

  currentPage = 0;

  // Date Filters
  startDate: Date | null = null;
  endDate: Date | null = null;


  constructor(
    private dialog: MatDialog,
    private immobilierService: ImmobilierService,
    private snackBar: MatSnackBar,
    private excelService: ExcelService,
    private pdfService: PdfService
  ) {

    this.dataSource = new MatTableDataSource(this.contracts);
  }

  ngOnInit(): void {
    this.loadContracts();
  }

  loadContracts() {
    this.immobilierService.getContracts().subscribe({
      next: (data) => {
        this.contracts = data;
        this.dataSource.data = this.contracts;
        if (this.paginator) {
          this.dataSource.paginator = this.paginator;
        }
        if (this.sort) {
          this.dataSource.sort = this.sort;
        }
      },
      error: (err) => {
        console.error(err);
        this.snackBar.open('Erreur lors du chargement des contrats', 'Fermer', { duration: 3000 });
      }
    });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // Custom filter predicate
    this.dataSource.filterPredicate = (data: Contract, filter: string) => {
      const searchString = filter.toLowerCase();
      const matchesSearch =
        data.contractNumber.toLowerCase().includes(searchString) ||
        (data.ownerName && data.ownerName.toLowerCase().includes(searchString)) ||
        (data.tenantBuyerName && data.tenantBuyerName.toLowerCase().includes(searchString)) ||
        (data.propertyReference && data.propertyReference.toLowerCase().includes(searchString));

      const matchesType = this.filterType ? data.type === this.filterType : true;
      const matchesStatus = this.filterStatus ? data.status === this.filterStatus : true;

      // Date Filter
      let matchesDate = true;
      if (this.startDate && this.endDate) {
        const contractDate = new Date(data.startDate);
        matchesDate = contractDate >= this.startDate && contractDate <= this.endDate;
      } else if (this.startDate) {
        const contractDate = new Date(data.startDate);
        matchesDate = contractDate >= this.startDate;
      } else if (this.endDate) {
        const contractDate = new Date(data.startDate);
        matchesDate = contractDate <= this.endDate;
      }

      return matchesSearch && matchesType && matchesStatus && matchesDate;
    };
  }

  applyFilter() {
    this.dataSource.filter = this.searchText.trim().toLowerCase();
    // Trigger re-filter for type/status
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
    // Force update filter predicate logic if needed, but assigning to filter usually triggers it.
    // However, since we use custom predicate that reads component properties, we might need to re-trigger.
    // A trick is to append a space or reset filter. 
    // Better way:
    this.dataSource.filter = this.searchText.trim().toLowerCase();
  }

  onFilterChange() {
    // Re-trigger filter
    this.dataSource.filter = this.searchText.trim().toLowerCase() || ' ';
    if (!this.searchText) {
      this.dataSource.filter = ' '; // Hack to trigger filterPredicate
    }
  }

  onDateChange() {
    this.dataSource.filter = '' + Math.random();
  }

  onPageChange(event: any): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
  }


  openAddDialog() {
    const dialogRef = this.dialog.open(AddEditContratDialogComponent, {
      width: '900px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadContracts();
      }
    });
  }

  openEditDialog(contract: Contract) {
    const dialogRef = this.dialog.open(AddEditContratDialogComponent, {
      width: '900px',
      disableClose: true,
      data: { contract: contract }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadContracts();
      }
    });
  }

  openDetailDialog(contract: Contract) {
    this.dialog.open(ContratDetailDialogComponent, {
      width: '800px',
      data: { contract: contract }
    });
  }

  exportToExcel(): void {
    const dataToExport = this.dataSource.filteredData.map(c => ({
      'N° Contrat': c.contractNumber,
      'Type': c.type,
      'Réf. Bien': c.propertyReference,
      'Propriétaire': c.ownerName,
      'Client': c.tenantBuyerName,
      'Date début': c.startDate,
      'Statut': c.status
    }));
    this.excelService.generateExcel(dataToExport, 'Liste_Contrats');
  }

  exportToPdf(): void {
    this.pdfService.generatePdf('contrats-table-container', 'Liste_Contrats');
  }

  printPage(): void {
    window.print();
  }
}

